import { useState, useEffect, createContext, useContext, useRef } from 'react';
import { AppLogger } from '@workivate/tho-web-shared';
import { OmnichannelChatSDK } from '@microsoft/omnichannel-chat-sdk';
import { getOmnichannelChatConfig } from '../../pages/chat/ccs/helpers/get-omnichannel-chat-config';
import { getChatSDKConfig } from '../../pages/chat/ccs/helpers/get-chat-sdk-config';
import {
  BroadcastService,
  BroadcastServiceInitialize,
} from '@microsoft/omnichannel-chat-components';
import FeatureKeys from 'app/constants/feature-keys';
import { isFeatureEnabled } from 'stores/features-list-store';

import {
  BroadcastEvent,
  TelemetryEvent,
} from '@microsoft/omnichannel-chat-widget/lib/types/common/telemetry/TelemetryConstants';

import { getChatLocale } from '../../pages/chat/ccs/helpers/get-chat-locale';
import { ccsChatLocalisedTexts } from '../../pages/chat/ccs/helpers/ccs-chat-localised-texts';
import { ILiveChatWidgetProps } from '@microsoft/omnichannel-chat-widget/lib/types/components/livechatwidget/interfaces/ILiveChatWidgetProps';
import {
  getLiveChatWidgetPropsDesktop,
  getLiveChatWidgetPropsMobile,
} from 'pages/chat/ccs/helpers/get-live-chat-widget-props';
import { useCounsellorForChat } from 'pages/appointments/containers/counsellor-profile/state/hooks';
import { useCcsPhoneNumber } from 'pages/chat/ccs/helpers/use-ccs-phone-number';
import { useRouterState } from 'router/router-state-provider';
import { MyCareRoutes } from 'pages/my-care/my-care.constants';
import { CCSChatRoutes } from 'pages/chat/ccs/mobile/ccs-chat.module';

const CHAT_PHONE_TAG = '{company_access_number}';

export type ContextValue = {
  startCCSChat: (counsellorId?: string) => void;
  ccsChatContainerVisible: boolean;
  setCCSChatContainerVisible: (ccsChatContainerVisible: boolean) => void;
  ccsChatUnreadCount: number;
  ccsChatIsLoading: boolean;
  ccsChatIsMobileVersion: boolean;
  ccsWebChatLoaded: boolean;
  ccsChatEnabled: boolean;
  initialiseChatSDK: (isMobile: boolean, counsellorId?: string) => void;
  liveChatWidgetProps: ILiveChatWidgetProps | null;
  resetChatWidget: () => void;
};

export const CCSChatContext = createContext<ContextValue>({
  startCCSChat: () => null,
  ccsChatContainerVisible: false,
  setCCSChatContainerVisible: () => null,
  ccsChatUnreadCount: 0,
  ccsWebChatLoaded: false,
  ccsChatIsLoading: false,
  ccsChatIsMobileVersion: false,
  ccsChatEnabled: true,
  initialiseChatSDK: () => null,
  liveChatWidgetProps: null,
  resetChatWidget: () => null,
});

export const useChatContext = () => useContext(CCSChatContext);

type CCSChatProviderProps = {
  children: React.ReactNode;
};

export const CCSChatProvider = ({ children }: CCSChatProviderProps) => {
  const ccsChatEnabled = isFeatureEnabled(
    FeatureKeys.CARE_COORDINATION_SPECIALIST_MESSAGING,
  );
  const { fetchCounsellorName } = useCounsellorForChat();
  const { ccsPhoneNumber } = useCcsPhoneNumber(phoneNumber => {
    replacePhoneNumberInOutOfOfficeMessage(phoneNumber);
    replacePhoneNumberInSystemMessage(phoneNumber);
  });
  const { currentRoute } = useRouterState();

  const [ccsChatContainerVisible, setCCSChatContainerVisible] = useState(false);
  const [ccsChatUnreadCount, setCCSChatUnreadCount] = useState(0);
  const [ccsChatIsLoading, setCCSChatIsLoading] = useState(false); // when the process of starting the chat is loading
  const [ccsWebChatLoaded, setCCSWebChatLoaded] = useState(false); // when the chat container has loaded
  const [ccsChatPreMessage, setCCSChatPreMessage] = useState<string | null>(
    null,
  );
  const [ccsChatSDK, setCCSChatSDK] = useState<OmnichannelChatSDK | null>(null);

  const ccsChatStarted = useRef(false);

  const [liveChatWidgetProps, setLiveChatWidgetProps] =
    useState<ILiveChatWidgetProps | null>(null);

  const initialiseChatSDK = async (
    isMobile: boolean,
    counsellorId?: string,
  ) => {
    setCCSChatIsLoading(true);
    setLiveChatWidgetProps(null);

    const omnichannelConfig = getOmnichannelChatConfig();
    const chatSDK = new OmnichannelChatSDK(
      omnichannelConfig,
      getChatSDKConfig(),
    );

    await chatSDK
      .initialize()
      .catch(e => AppLogger.error('Failed to initialize chat', e));

    if (chatSDK.isInitialized) {
      setCCSChatSDK(chatSDK);
      const liveChatCfg = await chatSDK.getLiveChatConfig();
      try {
        if (counsellorId) {
          const name = await fetchCounsellorName(counsellorId);
          if (name) {
            setCCSChatPreMessage(
              polyglot.t('ccs_chat.messages.counsellor_booking', {
                counsellor: name,
              }),
            );
          }
        }
      } catch (e: any) {
        AppLogger.error(e);
      }

      const chatLocale = getChatLocale().firstLocalePart;

      const wprop = isMobile
        ? getLiveChatWidgetPropsMobile(liveChatCfg, chatLocale, chatSDK)
        : getLiveChatWidgetPropsDesktop(liveChatCfg, chatLocale, chatSDK);

      setLiveChatWidgetProps(wprop);

      const broadcastServiceChannelName = `${omnichannelConfig?.widgetInstanceId}_${omnichannelConfig?.widgetId}`;
      BroadcastServiceInitialize(broadcastServiceChannelName);
    }
  };

  /**
   * BroadCastService is a pub/sub service channel for chat widget component communication
   * For more info see https://github.com/microsoft/omnichannel-chat-widget/blob/main/docs/Telemetry.md
   * The 'StartChat' event restarts the chat even when it's started already which is why we use ccsChatStarted
   */
  const startCCSChat = async (counsellorId?: string) => {
    if (!ccsChatIsLoading) {
      if (ccsChatStarted.current) {
        setCCSChatContainerVisible(!ccsChatContainerVisible);
      } else {
        await initialiseChatSDK(false, counsellorId);
        setCCSChatContainerVisible(true);
        ccsChatStarted.current = true;

        BroadcastService.postMessage({
          eventName: 'StartChat' as BroadcastEvent.StartChat,
        });
      }
    }
  };

  const resetChatWidget = () => {
    try {
      BroadcastService.postMessage({
        eventName:
          'InitiateEndChatOnBrowserUnload' as BroadcastEvent.InitiateEndChatOnBrowserUnload,
      });
    } catch (error) {
      /* empty */
    }

    setLiveChatWidgetProps(null);
  };

  /**
   * This is necessary because the library doesn't let us set this through props
   */
  const updateConfirmationPaneTexts = () => {
    const confirmationPaneConfirmBtn = document.querySelector(
      '#oc-lcw-confirmation-pane-confirmbutton',
    );
    const confirmationPaneCancelBtn = document.querySelector(
      '#oc-lcw-confirmation-pane-cancelbutton',
    );

    if (confirmationPaneConfirmBtn && confirmationPaneCancelBtn) {
      const { firstLocalePart } = getChatLocale();
      confirmationPaneConfirmBtn.innerHTML =
        ccsChatLocalisedTexts(firstLocalePart).dialogOptions.CLOSE;
      confirmationPaneCancelBtn.innerHTML =
        ccsChatLocalisedTexts(firstLocalePart).dialogOptions.CANCEL;
    }
  };

  const replacePhoneNumberInSystemMessage = (phoneNumber?: string) => {
    if (phoneNumber) {
      const messageNode = document.querySelector(
        '.webchat__basic-transcript__activity-markdown-body',
      );
      if (messageNode?.textContent?.includes(CHAT_PHONE_TAG)) {
        messageNode.textContent = messageNode.textContent.replace(
          CHAT_PHONE_TAG,
          phoneNumber,
        );
      }
    }
  };

  const replacePhoneNumberInOutOfOfficeMessage = (phoneNumber?: string) => {
    if (phoneNumber) {
      const outOfOfficeTextNode = document.querySelector(
        '#oc-lcw-outofofficehours-pane-title',
      );
      if (outOfOfficeTextNode?.textContent?.includes(CHAT_PHONE_TAG)) {
        outOfOfficeTextNode.textContent =
          outOfOfficeTextNode.textContent.replace(CHAT_PHONE_TAG, phoneNumber);
      }
    }
  };

  const processMessage = payload => {
    if (payload && ccsPhoneNumber) {
      const { messageType, text } = payload;
      if (messageType === 'system' && text.includes(CHAT_PHONE_TAG)) {
        setTimeout(() => {
          replacePhoneNumberInSystemMessage(ccsPhoneNumber);
        }, 10);
      }
    }
  };

  const ccsChatIsMobileVersion =
    currentRoute.name === CCSChatRoutes.CHAT ||
    currentRoute.name === MyCareRoutes.CHAT;

  useEffect(() => {
    setCCSChatUnreadCount(0);
    const subscription = BroadcastService.getMessageByEventName(
      'NewMessageReceived' as BroadcastEvent.NewMessageReceived,
    ).subscribe(event => {
      const { payload } = event;
      if (payload) {
        const { messageType } = payload;

        if (messageType === 'user' && !ccsChatContainerVisible) {
          setCCSChatUnreadCount(prevState => prevState + 1);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [ccsChatContainerVisible]);

  useEffect(() => {
    const eventSubcription = BroadcastService.getAnyMessage().subscribe(
      event => {
        const { payload, eventName } = event;

        switch (eventName) {
          case 'NewMessageReceived':
          case 'HistoryMessageReceived':
            processMessage(payload);
            break;
          case 'OutOfOfficePaneLoaded':
            setCCSChatIsLoading(false);
            replacePhoneNumberInOutOfOfficeMessage(ccsPhoneNumber);
            break;
          default:
            break;
        }
      },
    );

    return () => {
      eventSubcription.unsubscribe();
    };
  }, [ccsPhoneNumber]);

  useEffect(() => {
    const subscription = BroadcastService.getMessageByEventName(
      'HistoryMessageReceived' as BroadcastEvent.HistoryMessageReceived,
    ).subscribe(event => {
      const { payload } = event;
      if (payload) {
        const { messageType } = payload;

        if (messageType === 'system' && ccsChatPreMessage) {
          const sendAMessage = async () => {
            if (!ccsChatPreMessage || !ccsChatSDK) return;

            await ccsChatSDK
              .sendMessage({
                content: ccsChatPreMessage,
                timestamp: new Date(),
              })
              .then(() => setCCSChatPreMessage(null))
              .catch(e =>
                AppLogger.error('Failed to send ccsChatPreMessage', e),
              );
          };

          sendAMessage();
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [ccsChatPreMessage, ccsChatSDK?.isInitialized]);

  useEffect(() => {
    const subscription = BroadcastService.getMessageByEventName(
      'WebChatLoaded' as TelemetryEvent.WebChatLoaded,
    ).subscribe(() => {
      setCCSWebChatLoaded(true);
      setCCSChatIsLoading(false);
    });

    const endChatSub = BroadcastService.getMessageByEventName(
      'EndChatSDKCall' as TelemetryEvent.EndChatSDKCall,
    ).subscribe(() => {
      ccsChatStarted.current = false;
    });

    const confirmationPaneSub = BroadcastService.getMessageByEventName(
      'ConfirmationPaneLoaded' as TelemetryEvent.ConfirmationPaneLoaded,
    ).subscribe(() => {
      updateConfirmationPaneTexts();
    });

    const confirmationConfirmClicked = BroadcastService.getMessageByEventName(
      'ConfirmationConfirmButtonClicked' as TelemetryEvent.ConfirmationCancelButtonClicked,
    ).subscribe(() => {
      ccsChatStarted.current = false;
      setCCSChatContainerVisible(false);
    });

    return () => {
      subscription.unsubscribe();
      endChatSub.unsubscribe();
      confirmationPaneSub.unsubscribe();
      confirmationConfirmClicked.unsubscribe();
    };
  }, []);

  const ccsContext: ContextValue = {
    startCCSChat,
    ccsChatContainerVisible,
    setCCSChatContainerVisible,
    ccsChatUnreadCount,
    ccsWebChatLoaded,
    ccsChatEnabled,
    initialiseChatSDK,
    liveChatWidgetProps,
    resetChatWidget,
    ccsChatIsLoading,
    ccsChatIsMobileVersion,
  };

  return (
    <CCSChatContext.Provider value={ccsContext}>
      {children}
    </CCSChatContext.Provider>
  );
};
